/**
 * APSelectTrigger Component
 *
 * The trigger (button) component for the APSelect dropdown.
 * Uses a native <button> directly to avoid double-border from APButton wrapper.
 */

import React from 'react';

import APIconAstropay from '@shared/components/atoms/media/icon-ap';
import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPSelectTriggerProps } from './types';

// Arrow down icon component using APIconAstropay
const ArrowDownIcon = () => <APIconAstropay icon="down_line" size={16} color="currentColor" />;

// Clear icon component using APIconAstropay
const ClearIcon = () => <APIconAstropay icon="close_line" size={16} color="currentColor" />;

export const APSelectTrigger: React.FC<IAPSelectTriggerProps> = ({
  size,
  variant: _variant,
  isOpen,
  disabled,
  clearable = false,
  placeholder,
  selectedLabel,
  icon,
  className = '',
  onClick,
  onClear,
  onKeyDown,
}) => {
  const translate = useTranslate();

  // Build trigger classes using apds-select__trigger pattern
  const sizeClass = size === 'small' ? 'apds-select__trigger--small'
    : size === 'large' ? 'apds-select__trigger--large'
    : 'apds-select__trigger--medium';

  const triggerClasses = [
    'apds-select__trigger',
    sizeClass,
    isOpen && 'apds-select__trigger--focus',
    disabled && 'apds-select__trigger--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClearClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClear?.();
  };

  const showClearButton = clearable && selectedLabel && !disabled;

  return (
    <button
      type="button"
      className={triggerClasses}
      onClick={onClick}
      onKeyDown={onKeyDown}
      disabled={disabled}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
    >
      <div className="apds-select__trigger-content">
        {selectedLabel ? (
          <span className="apds-select__value-text">{selectedLabel}</span>
        ) : (
          <span className="apds-select__placeholder">{placeholder}</span>
        )}
      </div>

      {/* Clear button when clearable and has value */}
      {showClearButton && (
        <div
          className="apds-select__clear-button"
          onClick={handleClearClick}
          role="button"
          tabIndex={0}
          aria-label={translate('aria.clear-selection')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const syntheticEvent = { stopPropagation: () => {} } as React.MouseEvent;
              handleClearClick(syntheticEvent);
            }
          }}
        >
          <ClearIcon />
        </div>
      )}

      {/* Arrow icon */}
      <div className="apds-select__arrow">{icon || <ArrowDownIcon />}</div>
    </button>
  );
};

export default APSelectTrigger;
